import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { blue, green, red, yellow } from '@mui/material/colors';
import React, { useCallback } from 'react';
import { toast, type ToastOptions } from 'react-hot-toast';

export function useToastHelper(props?: { globalOptions?: ToastOptions }) {
  const globalOptions = props?.globalOptions;

  const sendSuccessToast = useCallback(
    (message: string | null | undefined, options?: ToastOptions) => {
      if (!message) return;

      return toast.success(message, {
        icon: <CheckCircleIcon color="inherit" fontSize="small" />,
        ...(globalOptions ?? {}),
        ...(options ?? {}),
        style: { backgroundColor: green[50], color: green[900] },
      });
    },
    [globalOptions],
  );

  const sendErrorToast = useCallback(
    (message: string | null | undefined, options?: ToastOptions) => {
      if (!message) return;
      return toast.error(message, {
        icon: <ErrorIcon color="inherit" fontSize="small" />,
        ...(globalOptions ?? {}),
        ...(options ?? {}),
        style: { backgroundColor: red[50], color: red[900] },
      });
    },
    [globalOptions],
  );

  const sendWarningToast = useCallback(
    (message: string | null | undefined, options?: ToastOptions) => {
      if (!message) return;
      return toast(message, {
        icon: <WarningIcon color="inherit" fontSize="small" />,
        ...(globalOptions ?? {}),
        ...(options ?? {}),
        style: { backgroundColor: yellow[50], color: yellow[900] },
      });
    },
    [globalOptions],
  );

  const sendInfoToast = useCallback(
    (message: string | null | undefined, options?: ToastOptions) => {
      if (!message) return;
      return toast(message, {
        icon: <InfoIcon color="inherit" fontSize="small" />,
        ...(globalOptions ?? {}),
        ...(options ?? {}),
        style: { backgroundColor: blue[50], color: blue[900] },
      });
    },
    [globalOptions],
  );

  const sendToast = useCallback(
    (
      message: string | null | undefined,
      opts: { appearance: 'success' | 'error' | 'warning' | 'info'; options?: ToastOptions },
    ) => {
      if (!message) return;
      const { appearance, options } = opts;
      const toasts = {
        success: sendSuccessToast,
        error: sendErrorToast,
        warning: sendWarningToast,
        info: sendInfoToast,
      };
      return toasts[appearance](message, options);
    },
    [sendSuccessToast, sendErrorToast, sendWarningToast, sendInfoToast],
  );

  return {
    sendSuccessToast,
    sendErrorToast,
    sendWarningToast,
    sendInfoToast,
    sendToast,
  };
}
